import { validateAddressFields } from "./address";
import {
  FILING_COMPANY_NAME_MESSAGE,
  hasFilingSafeCompanyNameCharacters,
} from "./company-name-characters";
import { RULE_CODES } from "./codes";
import { isEnglishPersonName, personNameMessage } from "./person-name";
import {
  FULL_OWNERSHIP_BASIS_POINTS,
  ownershipTotalBasisPoints,
  percentToBasisPoints,
} from "./ownership";
import { isPlausiblePhone, PHONE_MESSAGE } from "./phone";
import {
  normalizeTaxIdForComparison,
  normalizeTaxIdType,
  taxIdIssue,
} from "./tax-id";
import type { CompanyType, RuleViolation, ShareholderInput } from "./types";

export const MAX_SHAREHOLDERS = 50;

const MAX_EMAIL_LENGTH = 255;
/** Longest accepted person/company name on a shareholder record. */
export const MAX_NAME_LENGTH = 100;

// HTML5-style address grammar with a required TLD, matching what the Zod
// `.email()` check accepted in the Product schemas.
const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

function field(index: number, suffix: string): string {
  return `shareholders[${index}].${suffix}`;
}

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isTrue(value: unknown): boolean {
  return value === true;
}

/** Signing authority for an LLC means the explicit manager flag. */
function llcHasSigningAuthority(shareholder: ShareholderInput): boolean {
  return isTrue(shareholder.ownershipDetails?.signingAuthority);
}

/**
 * C-Corp forms do not expose a standalone signing-authority checkbox, so an
 * officer or a director satisfies the requirement.
 */
function ccorpHasSigningAuthority(shareholder: ShareholderInput): boolean {
  const details = shareholder.ownershipDetails;
  return (
    isTrue(details?.signingAuthority) ||
    isTrue(details?.officer) ||
    isTrue(details?.boardMember)
  );
}

export function hasSigningAuthority(
  shareholder: ShareholderInput,
  companyType: CompanyType,
): boolean {
  return companyType === "ccorp"
    ? ccorpHasSigningAuthority(shareholder)
    : llcHasSigningAuthority(shareholder);
}

/** Indexes of entries whose key already appeared earlier in the list. */
function duplicateIndexes(keys: (string | null)[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];

  keys.forEach((key, index) => {
    if (key === null) return;
    if (seen.has(key)) {
      duplicates.push(index);
      return;
    }
    seen.add(key);
  });

  return duplicates;
}

/** Required, then English letters only (V5). The message never carries the value. */
function personName(
  value: unknown,
  index: number,
  key: string,
  label: string,
): RuleViolation[] {
  const name = text(value);
  if (!name) {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_NAME_REQUIRED,
        field: field(index, key),
        message: `${label} is required`,
      },
    ];
  }
  if (!isEnglishPersonName(name)) {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_NAME_INVALID,
        field: field(index, key),
        message: personNameMessage(label),
      },
    ];
  }
  return [];
}

function validateIdentity(
  shareholder: ShareholderInput,
  index: number,
): RuleViolation[] {
  const kind = text(shareholder.ownershipType)?.toLowerCase();

  if (kind !== "individual" && kind !== "company") {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_TYPE_INVALID,
        field: field(index, "ownershipType"),
        message: "Shareholder must be an individual or a company",
      },
    ];
  }

  if (kind === "individual") {
    return [
      ...personName(shareholder.firstName, index, "firstName", "First name"),
      ...personName(shareholder.lastName, index, "lastName", "Last name"),
    ];
  }

  const violations: RuleViolation[] = [];
  const companyName = text(shareholder.companyName);
  if (!companyName) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_NAME_REQUIRED,
      field: field(index, "companyName"),
      message: "Company name is required",
    });
  } else if (!hasFilingSafeCompanyNameCharacters(companyName)) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_COMPANY_NAME_INVALID,
      field: field(index, "companyName"),
      message: FILING_COMPANY_NAME_MESSAGE,
    });
  }
  violations.push(
    ...personName(
      shareholder.authorizedPersonFirstName,
      index,
      "authorizedPersonFirstName",
      "Authorized person first name",
    ),
    ...personName(
      shareholder.authorizedPersonLastName,
      index,
      "authorizedPersonLastName",
      "Authorized person last name",
    ),
  );
  return violations;
}

function validateEmail(
  shareholder: ShareholderInput,
  index: number,
): RuleViolation[] {
  const email = text(shareholder.email);

  if (!email) {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_EMAIL_REQUIRED,
        field: field(index, "email"),
        message: "Email address is required",
      },
    ];
  }

  if (email.length > MAX_EMAIL_LENGTH || !EMAIL_PATTERN.test(email)) {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_EMAIL_INVALID,
        field: field(index, "email"),
        message: "Valid email address is required",
      },
    ];
  }

  return [];
}

/** Optional, but a phone that is there must carry its country code. */
function validatePhone(
  shareholder: ShareholderInput,
  index: number,
): RuleViolation[] {
  const phone = text(shareholder.phone);
  if (phone === null || isPlausiblePhone(phone)) return [];
  return [
    {
      code: RULE_CODES.SHAREHOLDER_PHONE_INVALID,
      field: field(index, "phone"),
      message: PHONE_MESSAGE,
    },
  ];
}

/**
 * The shareholder's own address: the per-country postal table (B4, B9), every
 * street/city/state/ZIP rule for a US address, and outside the US only the
 * city, the country and the postal code the country uses.
 */
function validateHomeAddress(
  shareholder: ShareholderInput,
  index: number,
): RuleViolation[] {
  return validateAddressFields(
    shareholder.address,
    (suffix) => field(index, `address.${suffix}`),
    { personal: true },
  );
}

function validateTaxId(
  shareholder: ShareholderInput,
  index: number,
): RuleViolation[] {
  const violations: RuleViolation[] = [];
  const taxId = text(shareholder.taxId);
  const taxIdType = normalizeTaxIdType(shareholder.taxIdType);

  if (!taxId) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_TAX_ID_REQUIRED,
      field: field(index, "taxId"),
      message: "Tax ID is required",
    });
  }

  if (taxIdType === null) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_TAX_ID_TYPE_INVALID,
      field: field(index, "taxIdType"),
      message: "Tax ID type must be SSN, EIN, ITIN, or FTIN",
    });
    return violations;
  }

  const issue = taxId ? taxIdIssue(taxId, taxIdType) : null;
  if (issue !== null) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_TAX_ID_INVALID,
      field: field(index, "taxId"),
      message: issue,
    });
  }

  return violations;
}

function validateOwnership(
  shareholder: ShareholderInput,
  index: number,
  companyType: CompanyType,
): RuleViolation[] {
  const details = shareholder.ownershipDetails;

  if (companyType === "llc") {
    const percentage = details?.percentage;

    if (typeof percentage !== "number" || Number.isNaN(percentage)) {
      return [
        {
          code: RULE_CODES.SHAREHOLDER_OWNERSHIP_REQUIRED,
          field: field(index, "ownershipDetails.percentage"),
          message: "Ownership percentage is required",
        },
      ];
    }

    if (percentToBasisPoints(percentage) <= 0 || percentage > 100) {
      return [
        {
          code: RULE_CODES.SHAREHOLDER_OWNERSHIP_INVALID,
          field: field(index, "ownershipDetails.percentage"),
          message:
            "Ownership percentage must be greater than 0% and at most 100%",
        },
      ];
    }

    return [];
  }

  const numberOfShares = details?.numberOfShares;

  if (typeof numberOfShares !== "number" || Number.isNaN(numberOfShares)) {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_SHARES_REQUIRED,
        field: field(index, "ownershipDetails.numberOfShares"),
        message: "Number of shares is required",
      },
    ];
  }

  if (!Number.isInteger(numberOfShares) || numberOfShares <= 0) {
    return [
      {
        code: RULE_CODES.SHAREHOLDER_SHARES_INVALID,
        field: field(index, "ownershipDetails.numberOfShares"),
        message: "All shareholders must have at least 1 whole share",
      },
    ];
  }

  return [];
}

/**
 * Every shareholder-level rule: identity, contact, tax ID, home address,
 * ownership, uniqueness, and the signing-authority requirement.
 */
export function validateShareholders(
  shareholders: ShareholderInput[],
  companyType: CompanyType,
): RuleViolation[] {
  if (!Array.isArray(shareholders) || shareholders.length === 0) {
    return [
      {
        code: RULE_CODES.SHAREHOLDERS_REQUIRED,
        field: "shareholders",
        message: "At least one shareholder is required",
      },
    ];
  }

  const violations: RuleViolation[] = [];

  if (shareholders.length > MAX_SHAREHOLDERS) {
    violations.push({
      code: RULE_CODES.SHAREHOLDERS_MAX_EXCEEDED,
      field: "shareholders",
      message: `Cannot have more than ${MAX_SHAREHOLDERS} shareholders`,
    });
  }

  shareholders.forEach((shareholder, index) => {
    violations.push(...validateIdentity(shareholder, index));
    violations.push(...validateEmail(shareholder, index));
    violations.push(...validatePhone(shareholder, index));
    violations.push(...validateTaxId(shareholder, index));
    violations.push(...validateHomeAddress(shareholder, index));
    violations.push(...validateOwnership(shareholder, index, companyType));
  });

  const emailKeys = shareholders.map((shareholder) => {
    const email = text(shareholder.email);
    return email === null ? null : email.toLowerCase();
  });
  for (const index of duplicateIndexes(emailKeys)) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_EMAIL_DUPLICATE,
      field: field(index, "email"),
      message:
        "Each shareholder needs their own email address. Change one of them.",
    });
  }

  const taxIdKeys = shareholders.map((shareholder) => {
    const taxId = text(shareholder.taxId);
    if (taxId === null) return null;
    const normalized = normalizeTaxIdForComparison(taxId);
    return normalized.length > 0 ? normalized : null;
  });
  for (const index of duplicateIndexes(taxIdKeys)) {
    violations.push({
      code: RULE_CODES.SHAREHOLDER_TAX_ID_DUPLICATE,
      field: field(index, "taxId"),
      message: "Two owners have the same tax ID. Check each owner's tax ID.",
    });
  }

  if (companyType === "llc") {
    const totalBasisPoints = ownershipTotalBasisPoints(
      shareholders.map(
        (shareholder) => shareholder.ownershipDetails?.percentage,
      ),
    );
    if (totalBasisPoints !== FULL_OWNERSHIP_BASIS_POINTS) {
      violations.push({
        code: RULE_CODES.LLC_OWNERSHIP_TOTAL_INVALID,
        field: "shareholders",
        message: `LLC ownership percentages must total 100%, currently ${(totalBasisPoints / 100).toFixed(2)}%`,
      });
    }
  }

  const signer = shareholders.some((shareholder) =>
    hasSigningAuthority(shareholder, companyType),
  );
  if (!signer) {
    violations.push({
      code: RULE_CODES.SIGNING_AUTHORITY_REQUIRED,
      field: "shareholders",
      message:
        companyType === "llc"
          ? "At least one shareholder must have signing authority (manager)"
          : "At least one shareholder must have signing authority",
    });
  }

  return violations;
}

/**
 * Delaware and Wyoming both require directors to be natural persons (spec
 * research, 2026-09-21). A company shareholder can sit on the board only
 * through its authorized person, who is the director of record.
 */
export const CCORP_DIRECTOR_PERSON_MESSAGE =
  "A director must be a person. A company shareholder can sit on the board only through its authorized person, so add that person's first and last name";

/**
 * Corporate-structure rules that apply only when forming a NEW C-Corp.
 * Existing C-Corps already have their board in place, so callers skip this.
 */
export function validateCCorpRoles(
  shareholders: ShareholderInput[],
): RuleViolation[] {
  if (!Array.isArray(shareholders) || shareholders.length === 0) {
    return [
      {
        code: RULE_CODES.SHAREHOLDERS_REQUIRED,
        field: "shareholders",
        message: "At least one shareholder is required",
      },
    ];
  }

  const violations: RuleViolation[] = [];

  if (!shareholders.some((sh) => isTrue(sh.ownershipDetails?.boardMember))) {
    violations.push({
      code: RULE_CODES.CCORP_BOARD_MEMBER_REQUIRED,
      field: "shareholders",
      message: "At least one shareholder must be a board member (director)",
    });
  }

  if (!shareholders.some((sh) => isTrue(sh.ownershipDetails?.officer))) {
    violations.push({
      code: RULE_CODES.CCORP_OFFICER_REQUIRED,
      field: "shareholders",
      message: "At least one shareholder must be an officer (president/CEO)",
    });
  }

  const secretaryCount = shareholders.filter((sh) =>
    isTrue(sh.ownershipDetails?.secretary),
  ).length;
  if (secretaryCount !== 1) {
    violations.push({
      code: RULE_CODES.CCORP_SECRETARY_REQUIRED,
      field: "shareholders",
      message: "Exactly one shareholder must be designated as secretary",
    });
  }

  shareholders.forEach((shareholder, index) => {
    const isCompany =
      text(shareholder.ownershipType)?.toLowerCase() === "company";
    if (!isCompany || !isTrue(shareholder.ownershipDetails?.boardMember)) {
      return;
    }
    if (
      text(shareholder.authorizedPersonFirstName) &&
      text(shareholder.authorizedPersonLastName)
    ) {
      return;
    }
    violations.push({
      code: RULE_CODES.CCORP_DIRECTOR_NATURAL_PERSON_REQUIRED,
      field: field(index, "ownershipDetails.boardMember"),
      message: CCORP_DIRECTOR_PERSON_MESSAGE,
    });
  });

  return violations;
}
